import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import clsx from 'clsx'
import BigNumber from 'bignumber.js'
import SortingIndicator from '@/components/SortingIndicator'
import IconToken from '@/components/icons/IconToken'
import IconDash from '@/components/icons/IconDash'
import IconArrowDown from '@/components/icons/IconArrowDown'
import {
  marketMapState,
  marketListState,
  markPriceMapState,
  marketTickerMapState,
  futuresMarketOpenInterestMapState
} from '@/recoil/Market'
import numberFormat from '@/utils/numberFormat'
import { alphabeticalSort, numericSort } from '@/utils/sorting'
import './Markets.css'

const MARKET_TYPE = {
  spot: 'SPOT',
  futures: 'PERP'
}

const FUTURES_MARKET_DATA_COLUMNS = ['openInterest']

const MARKETS_LIST_HEADER_COLUMNS = [
  {
    key: 'symbol',
    title: 'Market',
    align: 'left',
    defaultSortingOrder: 'ascending'
  },
  {
    key: 'price',
    title: 'Price (USDC)',
    align: 'left',
    defaultSortingOrder: 'descending'
  },
  {
    key: 'changePercentage',
    title: '24H Change% (USDC)',
    align: 'left',
    defaultSortingOrder: 'descending'
  },
  {
    key: 'volume',
    title: '24H Volume (USDC)',
    align: 'left',
    defaultSortingOrder: 'descending'
  },
  {
    key: 'openInterest',
    title: 'Open Interest (USDC)',
    align: 'left',
    defaultSortingOrder: 'descending',
    isFutures: true
  }
]

function toggleSortingOrder(sorting) {
  return sorting === 'ascending' ? 'descending' : 'ascending'
}

export default function Markets() {
  const navigate = useNavigate()
  const [marketMap] = useRecoilState(marketMapState)
  const [marketList] = useRecoilState(marketListState)
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const [marketTickerMap] = useRecoilState(marketTickerMapState)
  const [futuresMarketOpenInterestMap] = useRecoilState(
    futuresMarketOpenInterestMapState
  )

  const navigateMarket = useCallback(
    marketSymbol => navigate(`/trade/${marketSymbol}`),
    [navigate]
  )

  const [marketType, setMarketType] = useState(MARKET_TYPE.spot)
  const [sorting, setSorting] = useState({})
  const updateSorting = useCallback(
    (key, defaultSortingOrder) =>
      setSorting(prevSorting => {
        const sorting = { key }
        if (prevSorting.key === key) {
          if (prevSorting.order === defaultSortingOrder) {
            sorting.order = toggleSortingOrder(prevSorting.order)
          } else {
            return {}
          }
        } else {
          sorting.order = defaultSortingOrder
        }
        return sorting
      }),
    [setSorting]
  )

  useEffect(() => {
    if (
      marketType === MARKET_TYPE.spot &&
      FUTURES_MARKET_DATA_COLUMNS.includes(sorting.key)
    ) {
      setSorting({})
    }
  }, [marketType, sorting.key])

  const marketTickerReady = Object.keys(marketTickerMap).length > 0

  const marketDataList = marketList
    .map(symbol => {
      const { isFutures, baseCurrency, quoteCurrency, priceDecimal } =
        marketMap[symbol]
      const marketTickerNotFound = !marketTickerMap[symbol]
      const { open, close, amount } = marketTickerMap[symbol] || {}
      const markPrice = markPriceMap[symbol]
      const change = BigNumber(open).minus(close)
      const changePercentage = BigNumber(BigNumber(change).abs())
        .div(open)
        .times(100)
        .toNumber()
      const openInterest = futuresMarketOpenInterestMap[symbol]
      const openInterestInUsdc = BigNumber(openInterest)
        .times(markPrice)
        .toNumber()
      return marketTickerNotFound
        ? null
        : {
            symbol,
            isFutures,
            price: close,
            change,
            volume: amount,
            baseCurrency,
            quoteCurrency,
            priceDecimal,
            changePercentage,
            openInterestInUsdc
          }
    })
    .concat([])
    .filter(
      market =>
        market &&
        (marketType === MARKET_TYPE.spot ? !market.isFutures : market.isFutures)
    )

  const sortedMarketList = sorting.key
    ? marketDataList.sort((a, b) => {
        const { key, order } = sorting
        const isNumericSort = key !== 'symbol'
        const sortFunction = isNumericSort ? numericSort : alphabeticalSort
        const isAsc = order === 'ascending'

        return sortFunction(a[key], b[key], isAsc)
      })
    : marketDataList

  const MarketList = sortedMarketList.map(
    ({
      symbol,
      isFutures,
      price,
      change,
      volume,
      baseCurrency,
      quoteCurrency,
      priceDecimal,
      changePercentage,
      openInterestInUsdc
    }) => {
      return (
        <tr
          key={symbol}
          className="table-list-row"
          onClick={() => navigateMarket(symbol)}
        >
          <td className="table-list-cell align-left">
            <div className="market-symbol">
              <IconToken symbol={baseCurrency} />
              {isFutures ? (
                `${baseCurrency}-PERP`
              ) : (
                <>
                  {baseCurrency} /{' '}
                  <div className="market-quote-currency">{quoteCurrency}</div>
                </>
              )}
            </div>
          </td>
          <td className="table-list-cell align-left">
            {marketTickerReady && `$ ${numberFormat(price, priceDecimal)}`}
          </td>
          <td
            className={clsx('table-list-cell align-left price-change', {
              'color-positive': change.lt(0),
              'color-negative': change.gt(0),
              'no-change': change.eq(0)
            })}
          >
            <div className="price-change-row">
              <div
                className={clsx('price-change-icon', {
                  'is-up': change.lt(0)
                })}
              >
                {marketTickerReady &&
                  (change.eq(0) ? <IconDash /> : <IconArrowDown />)}
              </div>
              {marketTickerReady && (
                <div className="price-change-percentage">
                  {numberFormat(changePercentage, 2)}%
                </div>
              )}
              {marketTickerReady && `$ ${change.abs().toNumber()}`}
            </div>
          </td>
          <td className="table-list-cell align-left">
            {marketTickerReady &&
              `$ ${numberFormat(volume, { formatKey: 'max2', compact: true })}`}
          </td>
          {isFutures && (
            <td className="table-list-cell align-left">
              {marketTickerReady &&
                `$ ${numberFormat(openInterestInUsdc, {
                  formatKey: 'max2',
                  compact: true
                })}`}
            </td>
          )}
        </tr>
      )
    }
  )

  return (
    <div className="markets-container">
      <div className="card markets-list-container">
        <div className="markets-list-container-title">
          <div
            className={clsx('market-type-tab', {
              active: marketType === MARKET_TYPE.spot
            })}
            onClick={() => setMarketType(MARKET_TYPE.spot)}
          >
            Spot Markets
          </div>
          <div
            className={clsx('market-type-tab', {
              active: marketType === MARKET_TYPE.futures
            })}
            onClick={() => setMarketType(MARKET_TYPE.futures)}
          >
            Futures Markets
          </div>
        </div>
        <table className="table-list">
          <thead>
            <tr className="table-list-header">
              {MARKETS_LIST_HEADER_COLUMNS.map(
                ({ key, title, align, defaultSortingOrder, isFutures }) => {
                  return !isFutures || marketType === MARKET_TYPE.futures ? (
                    <th
                      key={key}
                      className={clsx('table-list-cell', `align-${align}`)}
                    >
                      <div
                        className="sorting-label noselect"
                        onClick={() => updateSorting(key, defaultSortingOrder)}
                      >
                        {title}
                        <SortingIndicator
                          isActive={sorting.key === key}
                          order={sorting.order}
                        />
                      </div>
                    </th>
                  ) : null
                }
              )}
            </tr>
          </thead>
          <tbody>{MarketList}</tbody>
        </table>
      </div>
    </div>
  )
}
